import React, { useState, useEffect, memo } from "react";
import InputAdornment from "@mui/material/InputAdornment";
import { getIn } from "formik";
import PropTypes from "prop-types";

import { ErrorMessage, ContexualHelp } from "./../../Notifications";
import { FieldDescription } from "../../FieldDescription/FieldDescription";
import { checkBrowserIsIE } from "../../../utils/checkBrowserIsIE";
import { areEqualFieldProps } from "../utils";

import { StyledFormControl, StyledTextField } from "./styled";
import { ICONS, Icon } from "../../Icons";
import { ALLOWED_CHAR_REGEX } from "../../../utils/validation";

const allowedCharRegex = ALLOWED_CHAR_REGEX;

const InputBase = ({
  contextualHelpText,
  contextualHelpProps = {},
  placement,
  disabled = false,
  placeholder = "",
  label,
  field,
  shrink,
  infoTitle,
  form,
  classes: extendedClasses = "",
  InputProps = {},
  ErrorMessageComponent = ErrorMessage,
  isLemnisk = false,
  totalLength,
  showCharLength = false,
  lemniskCall = () => {},
  showCounter = true,
  showEditIcon = false,
  iconColor = "grey",
  enableAllCharacters = false,
  warning = false,
  warningText = "",
  onChange,
  nestedFieldError = false,
  ...props
}) => {
  const { onBlur, ...fieldProps } = field || {};
  const { errors, touched, ...formRest } = form || {};
  const { inputProps = {}, ...inputPropsRest } = InputProps;
  const { showInLineError = false, ...otherInputProps } = inputProps;
  const { fieldValueLength, ...otherProps } = props;

  // Inject onWheel blur for number inputs to prevent scroll increment/decrement
  let enhancedInputProps = { ...otherInputProps };
  if ((props.type === "number" || fieldProps.type === "number") && !enhancedInputProps.onWheel) {
    enhancedInputProps.onWheel = e => e.target.blur();
  }

  const [hasFocus, setHasFocus] = useState(false);
  const [isFieldEditable, setIsFieldEditable] = useState(disabled);

  const errorMessage = errors ? getIn(errors, field?.name) : null;
  const isTouched = touched ? getIn(touched, field?.name) : false;
  const isError = errorMessage && isTouched;
  const isIE = checkBrowserIsIE();

  let fieldInputLength;

  if (fieldValueLength) {
    fieldInputLength = fieldValueLength;
  } else if (typeof field?.value === "string") {
    fieldInputLength = field.value.length;
  } else {
    fieldInputLength = 0;
  }

  const defaultOnChange = e => {
    if (formRest.setFieldValue && field?.name) {
      formRest.setFieldValue(field.name, e.target.value);
    }
  };

  const handleOnChange = onChange || defaultOnChange;

  const handleSpecialCharacterKeyPress = event => {
    if (!enableAllCharacters && event.data && allowedCharRegex.test(event.data)) {
      event.preventDefault();
    }
  };

  const handleSpecialCharacterPaste = event => {
    if (enableAllCharacters) return;

    event.preventDefault();
    const paste = (event.clipboardData || window.clipboardData).getData("text/plain");
    // Replace non-breaking spaces with regular spaces
    const textWithSpaces = paste.replace(/\xA0/g, " ");
    const regex = new RegExp(allowedCharRegex, "g");
    const sanitizedText = textWithSpaces.replace(regex, " ").trim();

    const input = event.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;

    // Get current value and insert sanitized text at selection
    const currentValue = input.value || "";
    const newValue = currentValue.slice(0, start) + sanitizedText + currentValue.slice(end);

    // Update the field value through Formik
    if (formRest.setFieldValue && field?.name) {
      formRest.setFieldValue(field.name, newValue);
    }

    // Update cursor position
    setTimeout(() => {
      const newCursorPosition = start + sanitizedText.length;
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleFocus = () => {
    if (isIE) {
      setHasFocus(true);
    }
  };

  const handleBlur = event => {
    if (field?.value && formRest.setFieldValue && field?.name) {
      formRest.setFieldValue(field.name, field.value.trim());
    }

    if (isLemnisk && field?.value) {
      lemniskCall(field.value);
    }

    if (isIE) {
      setHasFocus(false);
    }

    if (onBlur) {
      onBlur(event);
    }
  };

  useEffect(() => {
    setIsFieldEditable(disabled);
  }, [disabled]);

  const handleEditIconClick = () => {
    setIsFieldEditable(false);
  };

  const testId = props["data-testid"] || "input-field";

  return (
    <StyledFormControl data-testid={`${testId}Parent`} className={extendedClasses}>
      <ContexualHelp title={contextualHelpText} {...contextualHelpProps} placement={placement}>
        <StyledTextField
          {...fieldProps}
          {...otherProps}
          inputProps={{
            ...enhancedInputProps,
            "aria-invalid": !!isError,
            "aria-describedby": isError ? `${testId}-error` : undefined
          }}
          label={label}
          variant="filled"
          placeholder={placeholder}
          disabled={isFieldEditable}
          error={!!isError}
          onBeforeInput={handleSpecialCharacterKeyPress}
          onPaste={handleSpecialCharacterPaste}
          autoComplete="off"
          InputProps={{
            endAdornment: showEditIcon ? (
              <InputAdornment position="end" data-testid={`${testId}Adornment`}>
                <button
                  type="button"
                  onClick={handleEditIconClick}
                  onKeyPress={e => e.key === "Enter" && handleEditIconClick()}
                  tabIndex={0}
                  aria-label="Edit field"
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                >
                  <Icon name={ICONS.editIcon} />
                </button>
              </InputAdornment>
            ) : null,
            ...inputPropsRest,
            disableUnderline: true,
            className: !isError && warning ? "warning" : ""
          }}
          InputLabelProps={{
            shrink
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleOnChange}
          data-testid={testId}
        />
      </ContexualHelp>

      {isIE && !field?.value && hasFocus && (
        <span className="iePlaceholder" aria-hidden="true">
          {placeholder}
        </span>
      )}

      {(infoTitle || otherInputProps?.maxLength || props.maxLength) && (
        <FieldDescription
          title={infoTitle}
          fieldValueLength={fieldInputLength}
          fieldMaxLength={otherInputProps?.maxLength || props.maxLength}
          showInLineError={showInLineError}
          errorMessage={errorMessage}
          showWarning={warning}
          isError={isError}
          warningDesc={warningText}
          showCounter={showCounter}
        />
      )}

      {nestedFieldError && !isError && (
        <ErrorMessageComponent error={nestedFieldError} id={`${testId}-nested-error`} />
      )}

      {!showInLineError && isError && (
        <ErrorMessageComponent error={errorMessage} id={`${testId}-error`} />
      )}

      {!showInLineError && warning && !isError && (
        <p className="warningContainer" role="alert">
          {warningText}
        </p>
      )}
    </StyledFormControl>
  );
};

InputBase.propTypes = {
  contextualHelpText: PropTypes.string,
  contextualHelpProps: PropTypes.object,
  placement: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.any,
    onChange: PropTypes.func,
    onBlur: PropTypes.func
  }),
  shrink: PropTypes.bool,
  infoTitle: PropTypes.string,
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
    setFieldValue: PropTypes.func
  }),
  classes: PropTypes.string,
  InputProps: PropTypes.object,
  ErrorMessageComponent: PropTypes.elementType,
  isLemnisk: PropTypes.bool,
  totalLength: PropTypes.number,
  showCharLength: PropTypes.bool,
  lemniskCall: PropTypes.func,
  showCounter: PropTypes.bool,
  showEditIcon: PropTypes.bool,
  iconColor: PropTypes.string,
  enableAllCharacters: PropTypes.bool,
  warning: PropTypes.bool,
  warningText: PropTypes.string,
  onChange: PropTypes.func,
  nestedFieldError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  "data-testid": PropTypes.string,
  fieldValueLength: PropTypes.number,
  maxLength: PropTypes.number,
  type: PropTypes.string
};

export const Input = memo(InputBase, areEqualFieldProps);
